import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PrayerAttemptV1, PrayerLocalCommitResult } from '@/types/prayer-command';
import type { Prayer } from '@/types/prayer';
import { authorPrayerAttempt, getLocalContentEntity, listPrayerAttempts, revisePrayerAttempt } from '@/lib/content-lifecycle/store';
import { getCurrentContentLifecycleContext } from '@/lib/content-lifecycle/sync';
import { prayerFromCompleteDocument } from '@/lib/content-lifecycle/contracts';
import type { CompletePrayerDocument } from '@/types/content-lifecycle';
import { prayerIntentCurrentText, prayerIntentDraft, reviewPrayerIntent } from '@/lib/prayer-command';
import { prayerEditorAuth, publishPrayerCommit } from '@/lib/prayer-authoring';
import { settlePrayerUpdateDraft } from '@/lib/prayer-update-draft-store';
import { settlePrayScriptureDraft } from '@/lib/pray-scripture-draft-store';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getPrayer } from '@/lib/db';
import { getSupabase } from '@/lib/supabase';
import styles from './PrayerAttemptRecovery.module.css';

type Review = { attempt: PrayerAttemptV1; current: Prayer | null };
// A 'privacy' attempt can only come from an older build ("Hold this one close"
// is retired). It is discard-only and never authored, so the prayer and its
// stored flag stay exactly as they are.
const isRetiredSetting = (attempt: PrayerAttemptV1) => attempt.intent.kind === 'privacy';
export default function PrayerAttemptRecovery() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [error, setError] = useState<'conflict' | 'loadFailed' | null>(null);
  const [busy, setBusy] = useState(false);
  const latch = useRef(false);
  // A cold Settings render can precede OwnerScopedRuntime's auth-epoch init.
  // Capture once after initial resolution; later owner/epoch changes can only
  // invalidate this mounted review, never refresh its write authority.
  const authRef = useRef<ReturnType<typeof prayerEditorAuth> | null>(
    getAuthUserId() === undefined ? null : prayerEditorAuth(),
  );
  const [authActive, setAuthActive] = useState(Boolean(authRef.current?.ownerId));
  const currentAuth = () => Boolean(authRef.current?.ownerId) &&
    getAuthUserId() === authRef.current?.ownerId && getAuthEpoch() === authRef.current?.authEpoch;
  const load = useCallback(async () => {
    const auth = authRef.current;
    if (!auth?.ownerId) return;
    const attempts = await listPrayerAttempts(auth.ownerId);
    const next = await Promise.all(attempts.map(async attempt => {
      const [entity, projection] = await Promise.all([
        getLocalContentEntity(auth.ownerId, 'prayer', attempt.prayerId), getPrayer(attempt.prayerId),
      ]);
      const current = entity?.visibleDocument && !entity.pendingDeleteAt && !entity.heldReason && !entity.quarantineReason
        ? prayerFromCompleteDocument(entity.visibleDocument as CompletePrayerDocument, projection)
        : !entity && attempt.intent.kind === 'raw' && attempt.intent.pendingQueueId === projection?.pendingTranscriptionId ? projection ?? null : null;
      return { attempt, current };
    }));
    if (getAuthUserId() === auth.ownerId && getAuthEpoch() === auth.authEpoch) setReviews(next);
  }, []);
  useEffect(() => {
    let disposed = false;
    const subscription = getSupabase()?.auth.onAuthStateChange(() => {
      queueMicrotask(() => {
        if (disposed) return;
        const active = Boolean(authRef.current?.ownerId) &&
          getAuthUserId() === authRef.current?.ownerId && getAuthEpoch() === authRef.current?.authEpoch;
        setAuthActive(active);
        if (!active) setReviews([]);
      });
    }).data.subscription;
    void waitForAuthResolution().then(async () => {
      if (disposed) return;
      if (authRef.current === null) authRef.current = prayerEditorAuth();
      const auth = authRef.current;
      const active = Boolean(auth.ownerId) && getAuthUserId() === auth.ownerId && getAuthEpoch() === auth.authEpoch;
      setAuthActive(active);
      if (active) await load();
    }).catch(() => {
      if (!disposed && authRef.current?.ownerId && getAuthUserId() === authRef.current.ownerId && getAuthEpoch() === authRef.current.authEpoch) setError('loadFailed');
    });
    return () => { disposed = true; subscription?.unsubscribe(); };
  }, [load]);
  const act = async (review: Review, action: 'resume' | 'mine' | 'discard') => {
    const auth = authRef.current;
    if (action !== 'discard' && isRetiredSetting(review.attempt)) return;
    if (latch.current || !auth || isCoreReadOnly() || !currentAuth()) return;
    latch.current = true; setBusy(true); setError(null);
    try {
      const context = await getCurrentContentLifecycleContext(auth);
      let attempt = review.attempt;
      let committed: Extract<PrayerLocalCommitResult, { kind: 'committed' }> | null = null;
      if (action === 'discard') await revisePrayerAttempt(context, attempt, null);
      else {
        if (action === 'mine') {
          const intent = review.current && reviewPrayerIntent(attempt.intent, review.current);
          if (!intent) throw new Error('prayer-recovery-target-unavailable');
          const revised = await revisePrayerAttempt(context, attempt, intent);
          if (!revised) throw new Error('prayer-attempt-not-pending');
          attempt = revised;
        }
        if (isRetiredSetting(attempt)) throw new Error('prayer-attempt-retired');
        const result = await authorPrayerAttempt(context, attempt);
        if (result.kind !== 'committed') throw new Error('prayer-attempt-not-pending');
        committed = result;
        publishPrayerCommit(result);
      }
      // Observe only this completed action; never change its outcome.
      settlePrayerUpdateDraft({ auth, reviewed: review.attempt, executed: attempt, result: committed });
      settlePrayScriptureDraft({ auth, reviewed: review.attempt, executed: attempt, result: committed });
      if (currentAuth()) {
        setReviews(current => current.filter(item => item.attempt.commandId !== attempt.commandId));
        void load().catch(() => { if (currentAuth()) setError('loadFailed'); });
      }
    } catch { if (currentAuth()) { setError('conflict'); await load().catch(() => {}); } }
    finally { latch.current = false; if (currentAuth()) setBusy(false); }
  };
  if (!authActive || !currentAuth() || (!reviews.length && !error)) return null;
  const value = (review: Review, mine: boolean) => {
    const intent = review.attempt.intent;
    const text = mine ? prayerIntentDraft(intent) : review.current ? prayerIntentCurrentText(intent, review.current) : '';
    if (intent.kind === 'status') return t(`status.${text === 'In Progress' ? 'inProgress' : text.toLowerCase()}`);
    return text;
  };
  return <section className={styles.section}>
    <h2>{t('prayerRecovery.title')}</h2>
    <p>{t('prayerRecovery.body')}</p>
    {error && <p role="alert">{t(`prayerRecovery.${error}`)}</p>}
    {reviews.map(review => isRetiredSetting(review.attempt) ? <article key={review.attempt.commandId} className={styles.card}>
      <h3>{t(`prayerRecovery.operation.${review.attempt.intent.kind}`)}</h3>
      <p>{t('prayerRecovery.retiredSettingBody')}</p>
      <div className={styles.actions}>
        <button disabled={busy || isCoreReadOnly()} onClick={() => void act(review, 'discard')}>{t('prayerRecovery.discard')}</button>
      </div>
    </article> : <article key={review.attempt.commandId} className={styles.card}>
      <h3>{t(`prayerRecovery.operation.${review.attempt.intent.kind}`)}</h3>
      <h3>{t('prayerRecovery.current')}</h3>
      <p className={styles.words}>{review.current ? value(review, false) : t('prayerRecovery.unavailable')}</p>
      <h3>{t('prayerRecovery.draft')}</h3>
      <p className={styles.words}>{value(review, true)}</p>
      {review.attempt.intent.kind === 'testimony' && review.attempt.intent.answered && <div>
        <h3>{t('prayerRecovery.status')}</h3>
        <p>{t('prayerRecovery.current')}: {review.current ? t(`status.${review.current.status === 'In Progress' ? 'inProgress' : review.current.status.toLowerCase()}`) : t('prayerRecovery.unavailable')}</p>
        <p>{t('prayerRecovery.draft')}: {t('status.answered')}</p>
      </div>}
      {review.attempt.intent.kind === 'status' && review.attempt.intent.movingEntry && <div>
        <h3>{t('prayerRecovery.movingEntry')}</h3>
        <p className={styles.words}>{review.current?.progressHistory.find(entry => entry.id === (review.attempt.intent.kind === 'status' ? review.attempt.intent.movingEntry?.id : null))?.note ?? t('prayerRecovery.unavailable')}</p>
      </div>}
      <div className={styles.actions}>
        <button disabled={busy || isCoreReadOnly()} onClick={() => void act(review, 'resume')}>{t('prayerRecovery.resume')}</button>
        {review.current && reviewPrayerIntent(review.attempt.intent, review.current) && <button disabled={busy || isCoreReadOnly()} onClick={() => void act(review, 'mine')}>{t('prayerRecovery.keepMine')}</button>}
        <button disabled={busy || isCoreReadOnly()} onClick={() => void act(review, 'discard')}>{t('prayerRecovery.discard')}</button>
      </div>
    </article>)}
  </section>;
}
